/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, boolean, timestamp, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";

// Live Tutor Session Types
export const LIVE_TUTOR_SESSION_TYPES = {
  LEARNING: 'learning',
  PODCAST: 'podcast',
  GENERAL: 'general',
  CODING: 'coding',
  SCIENCE: 'science',
} as const;

export const LIVE_TUTOR_SESSION_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
} as const;

// Live Tutor Sessions - main session tracking table
export const liveTutorSessions = pgTable("live_tutor_sessions", {
  id: serial("id").primaryKey(),
  
  // Basic session info
  title: text("title").notNull(),
  description: text("description"),
  sessionType: text("session_type").notNull(), // learning, podcast, general, coding, science
  status: text("status").notNull().default('active'), // active, paused, completed, archived
  
  // User and context
  userId: integer("user_id").notNull(), // Owner of the session
  participantIds: integer("participant_ids").array(), // Other participants (for collaborative sessions)
  
  // Learning context (if applicable)
  learningActivityId: integer("learning_activity_id"), // Current learning activity
  learningPlanId: integer("learning_plan_id"), // Associated learning plan
  learningModuleId: integer("learning_module_id"), // Associated learning module
  learningPathId: integer("learning_path_id"), // Associated learning path
  topicIds: integer("topic_ids").array(), // Topics being discussed
  skillIds: integer("skill_ids").array(), // Skills being practiced
  
  // Podcast context (if applicable)
  podcastEpisodeTitle: text("podcast_episode_title"), // Episode title for podcast sessions
  podcastOutline: jsonb("podcast_outline"), // Current podcast outline state
  recordingUrl: text("recording_url"), // URL to recorded audio/video
  
  // AI Configuration
  aiModel: text("ai_model"), // Which AI model was used
  aiPersonality: text("ai_personality"), // Tutor personality/config used
  configPath: text("config_path"), // Path to the config file used
  systemPrompt: text("system_prompt"), // System prompt used for the session
  
  // Session state and progress
  currentSegmentIndex: integer("current_segment_index").default(0), // For structured sessions
  progressData: jsonb("progress_data"), // Session-specific progress tracking
  sessionData: jsonb("session_data"), // Arbitrary session state data
  
  // Timing and duration
  startedAt: timestamp("started_at").defaultNow(),
  lastActiveAt: timestamp("last_active_at").defaultNow(),
  pausedAt: timestamp("paused_at"),
  completedAt: timestamp("completed_at"),
  totalDuration: integer("total_duration").default(0), // Total active time in seconds
  
  // Metadata
  tags: text("tags").array(), // User-defined tags for organization
  isPublic: boolean("is_public").default(false), // Whether session can be shared
  isFavorite: boolean("is_favorite").default(false), // User favorited
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Live Tutor Session Messages - conversation history
export const liveTutorSessionMessages = pgTable("live_tutor_session_messages", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").notNull(),
  
  // Message content
  messageType: text("message_type").notNull(), // user, ai, system, tool_call, tool_response
  content: text("content").notNull(),
  metadata: jsonb("metadata"), // Additional message data (tool calls, audio URLs, etc.)
  
  // Message context
  speakerId: integer("speaker_id"), // User ID if human message, null for AI
  speakerName: text("speaker_name"), // Display name for the speaker
  
  // Timing
  timestamp: timestamp("timestamp").defaultNow(),
  sequenceNumber: integer("sequence_number").notNull(), // Order within session
  
  // Message state
  isEdited: boolean("is_edited").default(false),
  isDeleted: boolean("is_deleted").default(false),
  parentMessageId: integer("parent_message_id"), // For threaded conversations
  
  createdAt: timestamp("created_at").defaultNow(),
});

// Live Tutor Session Snapshots - periodic state saves
export const liveTutorSessionSnapshots = pgTable("live_tutor_session_snapshots", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").notNull(),
  
  // Snapshot data
  snapshotType: text("snapshot_type").notNull(), // auto, manual, checkpoint
  sessionState: jsonb("session_state").notNull(), // Complete session state
  messageCount: integer("message_count").notNull(), // Number of messages at snapshot
  
  // Context at snapshot time
  currentActivity: jsonb("current_activity"), // Current learning activity state
  aiContext: jsonb("ai_context"), // AI conversation context
  userProgress: jsonb("user_progress"), // User progress at this point
  
  // Metadata
  description: text("description"), // User-provided description for manual snapshots
  isBookmark: boolean("is_bookmark").default(false), // User-marked important points
  
  createdAt: timestamp("created_at").defaultNow(),
});

// Live Tutor Session Analytics - usage and performance tracking
export const liveTutorSessionAnalytics = pgTable("live_tutor_session_analytics", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").notNull(),
  
  // Usage metrics
  messageCount: integer("message_count").default(0),
  userMessageCount: integer("user_message_count").default(0),
  aiMessageCount: integer("ai_message_count").default(0),
  toolCallCount: integer("tool_call_count").default(0),
  
  // Engagement metrics
  averageResponseTime: integer("average_response_time"), // AI response time in ms
  userEngagementScore: integer("user_engagement_score"), // 1-10 based on interaction patterns
  sessionQualityScore: integer("session_quality_score"), // 1-10 based on learning outcomes
  
  // Learning outcomes (if applicable)
  skillsImproved: integer("skills_improved").array(), // Skills that showed improvement
  topicsCovered: integer("topics_covered").array(), // Topics discussed
  learningObjectivesMet: integer("learning_objectives_met").default(0),
  
  // Technical metrics
  errorCount: integer("error_count").default(0),
  averageLatency: integer("average_latency"), // Network latency in ms
  audioQualityScore: integer("audio_quality_score"), // 1-10 if audio was used
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Zod schemas for validation
export const insertLiveTutorSessionSchema = createInsertSchema(liveTutorSessions);
export const insertLiveTutorSessionMessageSchema = createInsertSchema(liveTutorSessionMessages);
export const insertLiveTutorSessionSnapshotSchema = createInsertSchema(liveTutorSessionSnapshots);
export const insertLiveTutorSessionAnalyticsSchema = createInsertSchema(liveTutorSessionAnalytics);

// TypeScript types
export type LiveTutorSession = typeof liveTutorSessions.$inferSelect;
export type NewLiveTutorSession = typeof liveTutorSessions.$inferInsert;
export type LiveTutorSessionMessage = typeof liveTutorSessionMessages.$inferSelect;
export type NewLiveTutorSessionMessage = typeof liveTutorSessionMessages.$inferInsert;
export type LiveTutorSessionSnapshot = typeof liveTutorSessionSnapshots.$inferSelect;
export type NewLiveTutorSessionSnapshot = typeof liveTutorSessionSnapshots.$inferInsert;
export type LiveTutorSessionAnalytics = typeof liveTutorSessionAnalytics.$inferSelect;
export type NewLiveTutorSessionAnalytics = typeof liveTutorSessionAnalytics.$inferInsert; 